import { el } from './dom.js';
import { locateOnFloorplan } from './floorPlanPosition.js';

/**
 * 間取りミニマップ。
 * 3D空間の中だけだと今どこにいるのか分からなくなるため、
 * 間取り図の上に現在地を出し、図から直接その部屋へ飛べるようにする。
 */
export function createFloorPlan(container, { floorplan, viewpoints, onSelect, editable = false, onPlace, collapsed = false }) {
  /** 視点名 → 間取り図上の相対座標(0〜1) */
  const marks = new Map(floorplan.marks.map((m) => [m.name, m]));

  /** 視点名 → ドット要素。印を付け直せるよう常に全視点ぶん作る */
  const dots = new Map(
    viewpoints.map((vp, index) => {
      const dot = el('button', {
        class: 'fp-dot',
        type: 'button',
        title: vp.name,
        'aria-label': vp.name,
        onClick: (event) => {
          event.stopPropagation();
          onSelect(vp, index);
        },
      });
      dot.dataset.name = vp.name;
      placeDot(dot, marks.get(vp.name));
      return [vp.name, dot];
    }),
  );

  const image = el('img', {
    class: 'fp-img',
    src: floorplan.image,
    alt: '間取り図',
    loading: 'lazy',
    decoding: 'async',
  });

  const youArrow = el('span', { class: 'fp-you-arrow' });
  const you = el('div', { class: 'fp-you', hidden: true, 'aria-hidden': 'true' }, youArrow);
  const currentRoom = el('div', { class: 'fp-current-room', 'aria-live': 'polite' }, '現在地を確認中');
  const stage = el('div', { class: 'fp-stage' }, [image, ...dots.values(), you, currentRoom]);
  const toggle = el('button', { class: 'fp-toggle', type: 'button' }, '間取り');
  const root = el('div', { class: 'fp' }, [toggle, stage]);

  if (editable) {
    root.classList.add('is-editable');
    // 視点作成モードでは、図をクリックした場所に現在の視点の印を置く
    stage.addEventListener('click', (event) => {
      const rect = image.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = clamp01((event.clientX - rect.left) / rect.width);
      const y = clamp01((event.clientY - rect.top) / rect.height);
      if (typeof onPlace === 'function') onPlace({ x: round3(x), y: round3(y) });
    });
  }

  toggle.addEventListener('click', () => {
    const open = root.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
  });
  // 画面が広いときは最初から開いておく。
  // ただし写真モードは写真を隠してしまうので畳んだままにする。
  if (!collapsed && window.matchMedia('(min-width: 900px)').matches) {
    root.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
  }

  container.appendChild(root);

  return {
    /** 現在いる部屋のドットを光らせる */
    setActive(name) {
      for (const [key, dot] of dots) dot.classList.toggle('is-active', key === name);
    },
    /** カメラまたは歩行キャラクターの現在地と向きを図面上に表示する */
    setPosition(position, forward) {
      const located = locateOnFloorplan(position, forward, viewpoints, marks);
      if (located === null) {
        you.hidden = true;
        currentRoom.textContent = '現在地を表示できません';
        return;
      }
      you.hidden = false;
      you.style.left = `${located.x * 100}%`;
      you.style.top = `${located.y * 100}%`;
      youArrow.style.transform = `rotate(${located.angle}deg)`;
      currentRoom.textContent = `現在地：${located.room}`;
    },
    /** 印を置き直す（視点作成モード用） */
    setMark(name, { x, y }) {
      if (!dots.has(name)) return;
      marks.set(name, { name, x, y });
      placeDot(dots.get(name), marks.get(name));
    },
    /** 現在の印を物件JSON用の形で取り出す */
    toJSON() {
      return {
        image: floorplan.image,
        marks: viewpoints
          .filter((vp) => marks.has(vp.name))
          .map((vp) => {
            const m = marks.get(vp.name);
            return { name: vp.name, x: m.x, y: m.y };
          }),
      };
    },
    /** 間取り図に印が無い視点がどれか（データ整備の確認用） */
    get missing() {
      return viewpoints.filter((vp) => !marks.has(vp.name)).map((vp) => vp.name);
    },
  };
}

/** 印が無い視点のドットは隠す */
function placeDot(dot, mark) {
  if (mark === undefined) {
    dot.hidden = true;
    return;
  }
  dot.hidden = false;
  dot.style.left = `${mark.x * 100}%`;
  dot.style.top = `${mark.y * 100}%`;
}

function clamp01(n) {
  return Math.min(Math.max(n, 0), 1);
}

function round3(n) {
  return Math.round(n * 1000) / 1000;
}
